import { Controller } from "@hotwired/stimulus"

import Flash from "lib/flash"
import ErrorTrack from "lib/error_track"
import i18n from "lib/i18n"
import { processImageFile } from "lib/images/uploads/image_handling"

const AVATAR_OPTIONS = {
  allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
  maxUploadSizeKB: 12 * 1024, // Maximum size the client will allow to try and resize
  sizeThresholdKB: 512, // If selected file is above this, try to resize if over the max dimensions
  maxWidth: 1024,
  maxHeight: 1024,
  quality: 0.82, // Quality we will use when resizing the image
}

// Goes on an avatar file input. Shrinks the picked image before the form is
// submitted and swaps it into the input, or clears the input and flashes why
// it was rejected. Dispatches avatar-upload:resized from the input once the
// resized file is in place, so a preview can follow it.
export default class extends Controller {
  #busy = false

  async resize() {
    const input = this.element
    const file = input.files?.[0]
    if (this.#busy || !file) return

    this.#busy = true
    try {
      const result = await processImageFile(file, AVATAR_OPTIONS)

      switch (result.status) {
        case "success": {
          const dt = new DataTransfer()
          dt.items.add(result.file)
          input.files = dt.files
          this.dispatch("resized", { detail: { file: result.file } })
          break
        }
        case "invalid_type":
          this.#reject(i18n.t("editorial.avatar.invalid_type"))
          break
        case "file_too_large":
          this.#reject(
            i18n.t("editorial.avatar.file_too_large", {
              maxSizeMB: Math.floor(result.maxSizeKB / 1024),
            }),
          )
          break
        case "resize_failed":
          this.#reject(i18n.t("editorial.avatar.resize_failed"))
          break
      }
    } catch (e) {
      ErrorTrack.notify(e, {
        context: "avatar_upload_error",
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      })
      this.#reject(i18n.t("editorial.avatar.processing_error"))
    } finally {
      this.#busy = false
    }
  }

  // Clear the input so the same file can be picked again
  #reject(message) {
    const input = this.element
    try {
      input.files = new DataTransfer().files
    } catch {
      input.value = ""
    }
    Flash.now("alert", message)
  }
}
